// components/Header.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { trackActivity } from "../utils/activityTracker";
import { getAlerts } from "../controllers/alertController";
import AlertBox from "./AlertBox";

const Header = () => {
  const navigate = useNavigate();
  
  let user = null;
  try {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      user = JSON.parse(storedUser);
    }
  } catch (error) {
    user = null;
  }

  const storedUsername =
    localStorage.getItem("username") ||
    user?.username ||
    user?.userName ||
    user?.email ||
    user?.emailAddress;

  // Determine if Bill Reconciliation tab should be shown (only for 'jawa')
  const showBillReconciliation = storedUsername === 'jawa';

  const navItems = [
    { to: "/live", label: "Live" },
    { to: "/historical", label: "Historical" },
    { to: "/info", label: "Info" },
    { to: "/customization", label: "Customization" },
    ...(showBillReconciliation ? [{ to: "/bill-reconciliation", label: "Bill Reconciliation" }] : []),
  ];
  const [unreadCount, setUnreadCount] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [liveDropdownOpen, setLiveDropdownOpen] = useState(false);

  // Fetch unread count on mount and every 60 seconds
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!storedUsername) return;
      try {
        const data = await getAlerts(storedUsername);
        setUnreadCount(data.unread || 0);
      } catch (e) {
        // silent fail
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [storedUsername]);

  const remoteLogo =
    storedUsername && !logoError
      ? `/api/get_logo?username=${storedUsername}`
      : null;

  const companyLogo =
    remoteLogo ||
    user?.logo ||
    user?.logoUrl ||
    user?.logoURL ||
    user?.companyLogo ||
    user?.clientLogo ||
    user?.brandingLogo;

  const profileImage =
    user?.profilePicture ||
    user?.profile_image ||
    user?.profileImage ||
    user?.profile_pic ||
    user?.avatar ||
    user?.avatarUrl ||
    user?.avatarURL ||
    user?.photo ||
    user?.photoUrl ||
    user?.photoURL ||
    user?.image ||
    user?.imageUrl ||
    user?.imageURL;

  const avatarImage = companyLogo || profileImage;

  const userInitial = user?.displayName?.charAt(0)?.toUpperCase();

  const handleLogout = () => {
    trackActivity({ action: "LOGOUT", tab_name: "Header" });
    // Clear all user data
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login"); // Redirect to login
  };

  return (
    <div style={styles.header}>
      <div style={styles.brandArea}>
        <a
          href="https://enfo.ai/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "none" }}
        >
          <div style={styles.logoWrapper}>
            <h2 style={styles.logo}>
              <span style={styles.logoPrimary}>En</span>
              <span style={styles.logoSecondary}>Viz</span>
            </h2>
          </div>
        </a>
        <nav style={styles.nav}>
          {navItems.map(({ to, label, end }) => {
            if (to === "/live") {
              const liveSubItems = [
                { key: "diagram", label: "Diagram View" },
                { key: "table", label: "Table View" },
                { key: "forecast", label: "Forecasting" },
              ];
              return (
                <div key={to} style={{ position: "relative" }}>
                  <NavLink
                    to={to}
                    end={end}
                    onClick={(e) => { e.preventDefault(); setLiveDropdownOpen((v) => !v); }}
                    style={({ isActive }) => ({
                      ...styles.link,
                      backgroundColor: isActive ? "#fff" : "transparent",
                      color: isActive ? "#1BA08E" : styles.link.color,
                      fontWeight: isActive ? 700 : 600,
                      boxShadow: isActive ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
                      border: isActive ? "1.5px solid #1BA08E" : "1.5px solid transparent",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    })}
                  >
                    {label} <span style={{ fontSize: 8, opacity: 0.6 }}>▼</span>
                  </NavLink>
                  {liveDropdownOpen && (
                    <>
                      <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }} onClick={() => setLiveDropdownOpen(false)}></div>
                      <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "#fff", borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 100, minWidth: 160, overflow: "hidden" }}>
                        {liveSubItems.map((item) => (
                          <div
                            key={item.key}
                            onClick={() => {
                              sessionStorage.setItem("enviz-pending-tab", item.key);
                              window.dispatchEvent(new CustomEvent("enviz-tab-change", { detail: item.key }));
                              navigate("/live");
                              setLiveDropdownOpen(false);
                            }}
                            style={{ padding: "10px 16px", fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", borderBottom: item.key !== "forecast" ? "1px solid #f1f5f9" : "none" }}
                            onMouseEnter={(e) => { e.target.style.background = "#f0fdf4"; e.target.style.color = "#1BA08E"; }}
                            onMouseLeave={(e) => { e.target.style.background = "#fff"; e.target.style.color = "#374151"; }}
                          >
                            {item.label}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                style={({ isActive }) => ({
                  ...styles.link,
                  backgroundColor: isActive ? "#fff" : "transparent",
                  color: isActive ? "#1BA08E" : styles.link.color,
                  fontWeight: isActive ? 700 : 600,
                  boxShadow: isActive ? "0 2px 8px rgba(27,160,142,0.10)" : undefined,
                  border: isActive ? "1.5px solid #1BA08E" : "1.5px solid transparent",
                  transition: "all 0.2s",
                })}
              >
                {label}
              </NavLink>
            );
          })}
        </nav>
      </div>
      <div style={styles.right}>
        {user && (
          <>
            <button
              onClick={(e) => {
                window.dispatchEvent(new Event("enviz-refresh"));
                const btn = e.currentTarget;
                btn.style.transform = "scale(0.92)";
                btn.style.opacity = "0.7";
                setTimeout(() => { btn.style.transform = "scale(1)"; btn.style.opacity = "1"; }, 200);
              }}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "1.5px solid transparent",
                background: "transparent",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                transition: "transform 0.2s, opacity 0.2s, all 0.2s",
              }}
            >
              Refresh
            </button>
            <div style={{ position: 'relative', marginRight: '10px' }}>
              <button
                onClick={() => setShowAlerts(!showAlerts)}
                style={styles.bellButton}
                title="Notifications"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                {unreadCount > 0 && (
                  <span style={styles.notificationBadge}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              <AlertBox
                isOpen={showAlerts}
                onClose={() => {
                  setShowAlerts(false);
                  // Refresh count when closing
                  if (storedUsername) {
                    getAlerts(storedUsername).then(data => {
                      setUnreadCount(data.unread || 0);
                    }).catch(() => { });
                  }
                }}
              />
            </div>
            <span style={styles.clientName}>
              {user?.client_name || user?.username}
            </span>
            {avatarImage ? (
              <img
                src={avatarImage}
                alt={user?.displayName || "Profile"}
                style={styles.avatar}
                onError={() => setLogoError(true)}
              />
            ) : (
              userInitial && (
                <div style={styles.avatarFallback}>{userInitial}</div>
              )
            )}
          </>
        )}
        <button onClick={handleLogout} style={styles.logoutButton}>
          Logout
        </button>
      </div>

    </div>
  );
};

const styles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    backgroundColor: "#1BA08E",
    color: "white",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    gap: "20px",
    flexWrap: "wrap",
  },
  brandArea: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
    flexWrap: "wrap",
  },
  logoWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#fff",
    padding: "6px 18px 6px 12px",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(27,160,142,0.07)",
  },
  logo: {
    margin: 0,
    fontSize: "24px",
    letterSpacing: "1px",
    display: "flex",
    alignItems: "baseline",
    gap: "2px",
  },
  logoPrimary: {
    color: "#1BA08E",
  },
  logoSecondary: {
    color: "#3D5DAA",
  },
  nav: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  link: {
    padding: "8px 14px",
    borderRadius: "6px",
    color: "#fff",
    transition: "all 0.2s",
    fontWeight: 600,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  logoutButton: {
    padding: "6px 18px",
    border: "1.5px solid #1BA08E",
    borderRadius: "5px",
    cursor: "pointer",
    background: "#fff",
    color: "#1BA08E",
    fontWeight: "bold",
    fontSize: "16px",
    boxShadow: "0 2px 8px rgba(27,160,142,0.10)",
    transition: "all 0.2s",
    marginLeft: "8px",
    letterSpacing: "0.5px",
  },
  avatar: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    objectFit: "contain",
    backgroundColor: "#fff",
    padding: "4px",
  },
  avatarFallback: {
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    backgroundColor: "#1BA08E",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    color: "#fff",
  },
  user: {
    fontWeight: "bold",
  },
  clientName: {
    color: "#fff",
    fontWeight: "600",
    fontSize: "16px",
  },
  bellButton: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#fff',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background-color 0.2s',
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    minWidth: '16px',
    height: '16px',
    backgroundColor: '#ff4d4f',
    borderRadius: '8px',
    border: '1.5px solid #1BA08E',
    color: '#fff',
    fontSize: '10px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
  },
};

export default Header;
